import {
  Github,
  CheckCircle2,
  Clock,
  FileText,
  ExternalLink,
  Eye,
  Download,
  MessageSquareQuote,
  AlertCircle,
  Check,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { formatInstituteDate } from '@/features/teacher/utils';
import { shortWeekday } from '../../utils';
import { getSubjectStyle } from './homeworkTheme';
import type { StudentWorkItem } from './workItem';

interface HomeworkDetailsModalProps {
  viewing: StudentWorkItem | null;
  onClose: () => void;
  onDownloadSubmission: (work: StudentWorkItem) => void;
  onPreviewSubmission: (work: StudentWorkItem) => void;
}

export function HomeworkDetailsModal({
  viewing,
  onClose,
  onDownloadSubmission,
  onPreviewSubmission,
}: HomeworkDetailsModalProps) {
  if (!viewing) return null;

  const isSubmitted = viewing.submission !== null;
  const submission = viewing.submission;
  const theme = getSubjectStyle(viewing.subject.name);
  const isScored = submission?.score !== null && submission?.score !== undefined;

  return (
    <Modal
      isOpen={Boolean(viewing)}
      onClose={onClose}
      size="lg"
      title={
        <div className="flex flex-col gap-2 pt-0.5">
          <div className="flex flex-wrap items-center gap-2">
            {/* Subject badge with matching theme */}
            <span
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-xl text-xs font-bold uppercase tracking-wider ${theme.textAccent} bg-slate-100 dark:bg-white/10`}
            >
              <span className="grid h-4 w-4 place-items-center [&>svg]:h-3.5 [&>svg]:w-3.5">{theme.icon}</span>
              <span>{viewing.subject.name}</span>
            </span>

            {/* Submission / Grade status pill */}
            {isSubmitted ? (
              isScored ? (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-emerald-50 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800/60">
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  <span>Graded</span>
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-teal-50 text-teal-700 dark:bg-teal-950/60 dark:text-teal-300 border border-teal-200 dark:border-teal-800/60">
                  <Check className="h-3.5 w-3.5" />
                  <span>Submitted</span>
                </span>
              )
            ) : (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-amber-50 text-amber-700 dark:bg-amber-950/60 dark:text-amber-300 border border-amber-200 dark:border-amber-800/60">
                <Clock className="h-3.5 w-3.5" />
                <span>Not Handed In</span>
              </span>
            )}

            {viewing.typeLabel && (
              <span className="px-2.5 py-1 rounded-xl text-xs font-bold bg-slate-100 dark:bg-white/10 text-slate-700 dark:text-slate-300">
                {viewing.typeLabel}
              </span>
            )}
          </div>

          <h3 className="text-lg font-bold text-slate-900 dark:text-white leading-snug">
            {viewing.title}
          </h3>
        </div>
      }
      description={
        <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500 dark:text-slate-400 font-medium">
          <span>
            Set by <strong className="font-semibold text-slate-700 dark:text-slate-200">{viewing.assignedBy}</strong>
          </span>
          <span>•</span>
          <span className="font-semibold">
            Assigned: <span className='font-bold text-slate-700 dark:text-slate-200'>{shortWeekday(viewing.assignedOn)}, {formatInstituteDate(viewing.assignedOn)}</span>
          </span>
          <span>•</span>
          <span className="font-semibold">
            Due: <span className='font-bold text-slate-700 dark:text-slate-200'>{shortWeekday(viewing.dueDate)}, {formatInstituteDate(viewing.dueDate)}</span> 
          </span>
        </div>
      }
      footer={
        <div className="flex flex-col-reverse sm:flex-row items-center justify-end w-full gap-2.5">
          <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
            {submission?.fileName && (
              <>
                <Button
                  variant="secondary"
                  size="sm"
                  className="inline-flex items-center gap-1.5 rounded-xl font-bold cursor-pointer text-slate-700 dark:text-slate-200"
                  onClick={() => onPreviewSubmission(viewing)}
                >
                  <Eye className="h-3.5 w-3.5 text-indigo-500" />
                  <span>Preview</span>
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  className="inline-flex items-center gap-1.5 rounded-xl font-bold cursor-pointer text-slate-700 dark:text-slate-200"
                  onClick={() => onDownloadSubmission(viewing)}
                >
                  <Download className="h-3.5 w-3.5 text-indigo-500" />
                  <span>Download what I handed in</span>
                </Button>
              </>
            )}
          </div>
          
        </div>
      }
    >
      <div className="flex flex-col gap-4 text-sm pt-1">
        {/* SUBMISSION CARD (HIGHLIGHTED FOR SUBMITTED HOMEWORKS) */}
        {isSubmitted && submission && (
          <div className="rounded-2xl border border-emerald-200/90 dark:border-emerald-800/50 bg-gradient-to-br from-emerald-50/70 via-white to-teal-50/30 dark:from-emerald-950/30 dark:via-[#1b1d34] dark:to-teal-950/20 p-4 sm:p-5 shadow-xs flex flex-col gap-3.5">
            {/* Top row: Status header + score showcase */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-emerald-200/60 dark:border-emerald-800/40 pb-3">
              <div className="flex items-center gap-3">
                <div className="grid h-10 w-10 place-items-center rounded-xl bg-emerald-100 dark:bg-emerald-900/50 text-emerald-700 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-700/60 shrink-0 shadow-2xs">
                  <CheckCircle2 className="h-5 w-5 stroke-[2.5]" />
                </div>
                <div>
                  <h4 className="text-sm font-bold text-slate-900 dark:text-white">
                    Submission Details
                  </h4>
                  <p className="text-xs text-slate-500 dark:text-slate-400 font-medium">
                    Submitted on {formatInstituteDate(submission.handedInOn)}
                  </p>
                </div>
              </div>

              {/* Score Display */}
              {isScored ? (
                <div className="flex items-center gap-2 self-start sm:self-auto bg-white dark:bg-[#151728] border border-emerald-300 dark:border-emerald-700/80 px-3.5 py-1.5 rounded-xl shadow-2xs">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                    {viewing.scoreUnit === 'points' ? 'Your Points' : 'Your Score'}:
                  </span>
                  <span className="text-base font-black text-emerald-600 dark:text-emerald-400">
                    {submission.score}
                  </span>
                  {viewing.maxScore !== null && (
                    <span className="text-xs font-semibold text-slate-400">
                      / {viewing.maxScore}
                    </span>
                  )}
                </div>
              ) : (
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800/50 text-amber-700 dark:text-amber-300 text-xs font-bold self-start sm:self-auto">
                  <Clock className="h-3.5 w-3.5 text-amber-500" />
                  Not marked yet
                </span>
              )}
            </div>

            {/* Handed in file or link */}
            {submission.fileName ? (
              <div className="rounded-xl bg-white dark:bg-[#151728] border border-slate-200/90 dark:border-white/10 p-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3 shadow-2xs">
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <div className="grid h-9 w-9 place-items-center rounded-lg bg-indigo-50 dark:bg-indigo-950/50 text-indigo-600 dark:text-indigo-400 shrink-0">
                    <FileText className="h-4 w-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-bold text-slate-800 dark:text-slate-200 truncate">
                      {submission.fileName}
                    </p>
                    <p className="text-[11px] text-slate-400 font-medium">Attached Solution</p>
                  </div>
                </div>

                
              </div>
            ) : submission.repoUrl ? (
              <div className="rounded-xl bg-white dark:bg-[#151728] border border-slate-200/90 dark:border-white/10 p-3 flex items-center justify-between gap-3 shadow-2xs">
                <div className="flex items-center gap-2.5 min-w-0 flex-1">
                  <Github className="h-4 w-4 text-slate-800 dark:text-slate-200 shrink-0" />
                  <span className="text-xs font-bold text-slate-800 dark:text-slate-200 truncate">
                    {submission.repoUrl}
                  </span>
                </div>
                <a
                  href={submission.repoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-900 text-white hover:bg-slate-800 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100 text-xs font-bold transition-colors shrink-0"
                >
                  <span>Open Repo</span>
                  <ExternalLink className="h-3.5 w-3.5" />
                </a>
              </div>
            ) : (
              <div className="text-xs text-slate-600 dark:text-slate-400 font-medium">
                Submitted on paper in class.
              </div>
            )}

            {/* Note to teacher if provided */}
            {submission.studentNotes && (
              <div className="rounded-xl bg-slate-50 dark:bg-white/5 border border-slate-200/90 dark:border-white/10 p-3 flex flex-col gap-1 shadow-2xs">
                <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                  Note to Teacher:
                </span>
                <p className="text-xs text-slate-800 dark:text-slate-200 italic leading-relaxed">
                  &ldquo;{submission.studentNotes}&rdquo;
                </p>
              </div>
            )}

            {/* Teacher Feedback Quote Banner */}
            {submission.feedback ? (
              <div className="rounded-xl bg-white dark:bg-[#151728] border border-emerald-200 dark:border-emerald-800/50 p-3.5 flex items-start gap-3 shadow-2xs">
                <MessageSquareQuote className="h-5 w-5 text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5" />
                <div className="flex-1">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-emerald-700 dark:text-emerald-400">
                    Teacher Feedback:
                  </span>
                  <p className="text-xs sm:text-sm font-medium text-slate-800 dark:text-slate-200 italic mt-0.5 leading-relaxed">
                    &ldquo;{submission.feedback}&rdquo;
                  </p>
                </div>
              </div>
            ) : !isScored ? (
              <div className="rounded-xl bg-slate-50/80 dark:bg-white/5 border border-slate-200/80 dark:border-white/10 p-3 text-xs text-slate-500 dark:text-slate-400 font-medium">
                Your teacher has received your submission and has not marked it yet.
              </div>
            ) : null}
          </div>
        )}

        {/* NOT HANDED IN NOTICE (IF NOT SUBMITTED) */}
        {!isSubmitted && (
          <div className="rounded-2xl border border-amber-200 dark:border-amber-800/50 bg-amber-50/60 dark:bg-amber-950/20 p-4 flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
            <div>
              <h4 className="text-sm font-bold text-amber-900 dark:text-amber-200">
                Not handed in yet
              </h4>
              <p className="text-xs text-amber-800 dark:text-amber-300 mt-0.5 leading-relaxed">
                {viewing.submissionFormat === 'REPO_URL'
                  ? 'Use the Submit repo link button on the card to send your teacher the repository you worked in.'
                  : viewing.kind === 'ACTIVITY'
                    ? 'Use the Hand in work button on the card to send your work to your teacher.'
                    : 'Use the Upload Solution button on the card to send your work to your teacher.'}
              </p>
            </div>
          </div>
        )}

        {/* INSTRUCTIONS / WHAT TO DO CARD */}
        <div className="rounded-2xl bg-slate-50 dark:bg-[#151728] border border-slate-200/80 dark:border-white/10 p-4 flex flex-col gap-1.5">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
            <FileText className="h-3.5 w-3.5 text-indigo-500" />
            <span>Instructions / What to do</span>
          </div>
          <p className="text-xs sm:text-sm text-slate-800 dark:text-slate-200 whitespace-pre-line leading-relaxed font-normal">
            {viewing.details || 'No additional instructions provided for this task.'}
          </p>
        </div>
      </div>
    </Modal>
  );
}
